import { useEffect, type FC, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { Button, Icon, Tabs } from 'shared/ui'
import { getLabelAsNumberInternationalFormat } from 'shared/lib'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { useEntitiesContext } from 'entities/EntitiesExport'
import type { IContactAll } from 'entities/Contacts'
import {
  ContactsSearchListVariantEnum,
  ContactsSearchParentStore,
  ContactsSearchVariantEnum,
  type IIntegrationsTabKey,
  SELECTED_TAB_KEY,
} from 'widgets/ContactsSearch/store/contactsSearchParentStore'

import { minSearchCountText } from 'widgets/constants'
import { ContactsSearchList } from 'widgets/ContactsSearch/ui/ContactsSearchList/ContactsSearchList'
import styles from './styles.module.scss'

export type IContactsSearchProps = {
  search?: string
  focusedSearch?: boolean
  maxHeight?: number | string
  minHeight?: number | string
  onChange?: (item: IContactAll, params?: { isNew?: boolean }) => void
  onErrorCreate?: () => void
  onCloseMenu?: () => void
  onChangeLoader?: (status: boolean) => void
  withBorder?: boolean
  disabledHelp?: boolean
  disabledAddNumber?: boolean
  variant?: ContactsSearchVariantEnum
  selectedMap?: Map<number, IContactAll>
  onClearSelected?: () => void
  paramsCreateContact?: {
    forLink?: boolean
    fromConversation?: boolean
  }
  hiddenIntegrationsTabs?: IIntegrationsTabKey[]
  isAutomaticallyCreateNewContact?: boolean
  isHideOnLoading?: boolean
  wrapContentClassName?: string
  contactCreateBtnClassName?: string
}

export const ContactsSearch: FC<IContactsSearchProps> = observer(
  ({
    search = '',
    focusedSearch = false,
    onChange = () => {},
    onErrorCreate = () => {},
    onCloseMenu = () => {},
    onChangeLoader = () => {},
    onClearSelected = () => {},
    withBorder,
    selectedMap = new Map(),
    maxHeight,
    minHeight,
    disabledHelp = false,
    disabledAddNumber = false,
    paramsCreateContact,
    variant = ContactsSearchVariantEnum.default,
    hiddenIntegrationsTabs = [],
    isAutomaticallyCreateNewContact,
    isHideOnLoading,
    wrapContentClassName,
    contactCreateBtnClassName,
  }) => {
    const entities = useEntitiesContext()
    const { userPermissionsStore } = entities
    const [store] = useState<ContactsSearchParentStore>(
      () => new ContactsSearchParentStore(entities)
    )
    const {
      allowedIntegrations,
      loadingContacts,
      showTypeView,
      creatingNumberLoading,
      searchNumber,
      activeTab,
      tabs,
      loadingContactsLocal,
      selectedCount,
      hasSelected,
      canSearch,
      init,
      initTabs,
      initSelectedMap,
      handleInputSearch,
      handleAddItem,
      handleCreateContactSearchNumber,
      toggleSelectedTab,
      handleSelectTab,
      handleClearSelected,
    } = store

    useEffect(() => {
      init({
        disabledHelp: disabledHelp,
        disabledAddNumber: disabledAddNumber,
        hiddenIntegrationsTabs,
        paramsCreateContact: {
          forLink: paramsCreateContact?.forLink !== undefined ? paramsCreateContact.forLink : true,
          fromConversation:
            paramsCreateContact?.fromConversation !== undefined
              ? paramsCreateContact.fromConversation
              : true,
        },
      })
      initSelectedMap(selectedMap)
      initTabs(hiddenIntegrationsTabs)

      return () => {
        store.reset()
      }
    }, [])

    useEffect(() => {
      handleInputSearch(search)
    }, [search])

    useEffect(() => {
      onChangeLoader(loadingContacts)
    }, [loadingContacts])

    useEffect(() => {
      if (
        isAutomaticallyCreateNewContact &&
        !disabledAddNumber &&
        showTypeView === ContactsSearchListVariantEnum.create_contact &&
        !creatingNumberLoading
      ) {
        handleCreateNewContact()
      }
    }, [isAutomaticallyCreateNewContact])

    const handleClearSelect = () => {
      handleClearSelected()
      onClearSelected()
    }

    const handleCreateNewContact = () => {
      if (userPermissionsStore.getItem('can_create_contacts')) {
        handleCreateContactSearchNumber(onChange, onErrorCreate)
      } else {
        new FeatureRestrictedModalStore('Can create Contacts')
        onCloseMenu?.()
      }
    }

    const showSearchHelpText =
      !disabledHelp &&
      (showTypeView === ContactsSearchListVariantEnum.help || (!canSearch && focusedSearch))

    if (isHideOnLoading && loadingContacts) {
      return null
    }

    return (
      <div
        className={classnames(styles.content, {
          [styles.loading]: loadingContacts,
        })}
      >
        {showSearchHelpText && (
          <div className={classnames(styles.help, withBorder && styles.contentBorder)}>
            {minSearchCountText}
          </div>
        )}

        {!disabledAddNumber && showTypeView === ContactsSearchListVariantEnum.create_contact && (
          <div className={classnames(withBorder && styles.contentBorder)}>
            <div
              className={classnames(
                styles.button,
                {
                  [styles.disabled]: creatingNumberLoading,
                },
                contactCreateBtnClassName
              )}
              onClick={handleCreateNewContact}
            >
              <div className={styles.button__icon}>
                <Icon icon={'add'} fontSize={18} />
              </div>
              <div className={styles.button__text}>
                Add {getLabelAsNumberInternationalFormat(searchNumber)}
              </div>
            </div>
          </div>
        )}
        {(showTypeView === ContactsSearchListVariantEnum.list_contacts ||
          showTypeView === ContactsSearchListVariantEnum.selected_contacts) && (
          <div className={classnames(withBorder && styles.contentBorder, wrapContentClassName)}>
            {canSearch && (
              <Tabs
                tabs={tabs}
                activeTabKey={activeTab}
                handleSelectTab={handleSelectTab}
                withStopPropagation
              />
            )}
            {allowedIntegrations.map(({ key, store: integrationStore }) => {
              if (activeTab !== key) return null

              return (
                <ContactsSearchList
                  key={key}
                  store={integrationStore}
                  parentStore={store}
                  handleAddItem={(contact) => {
                    if (!userPermissionsStore.getItem('can_create_contacts')) {
                      const existInSalesmsg = !!allowedIntegrations
                        .find((integration) => integration.key === 'salesmsg')
                        ?.store?.items?.find((contact) => contact.id === contact.id)

                      if (existInSalesmsg) {
                        handleAddItem(contact, onChange, variant)
                      } else {
                        new FeatureRestrictedModalStore('Can create Contacts')
                        onCloseMenu?.()
                      }
                    } else {
                      handleAddItem(contact, onChange, variant)
                    }
                  }}
                  variant={variant}
                  search={search}
                  maxHeight={maxHeight}
                  minHeight={minHeight}
                  loadingParent={loadingContactsLocal}
                />
              )
            })}
          </div>
        )}

        {variant === ContactsSearchVariantEnum.checkbox && (
          <div className={styles.actionsFooter}>
            <Button
              disabled={!hasSelected}
              typeBtn={'text'}
              text={'Clear'}
              onClick={handleClearSelect}
            />
            <Button
              disabled={!hasSelected}
              active={activeTab === SELECTED_TAB_KEY}
              typeBtn={'informative'}
              text={`${selectedCount} selected`}
              onClick={toggleSelectedTab}
            />
          </div>
        )}
      </div>
    )
  }
)
